import type { Channel, Movie, Series } from '../types/iptv';
import { extractChannelQualityInfo } from './channelQualityUtils';

export interface DiffItem {
  id: string;
  name: string;
  changes?: string[];
}

export interface CategoryDiff {
  newLive: string[];
  newVod: string[];
  newSeries: string[];
}

export interface ListDiff {
  added: DiffItem[];
  changed: DiffItem[];
  removed: DiffItem[];
}

export interface CatalogDiff {
  channels: ListDiff;
  movies: ListDiff;
  series: ListDiff;
  categories: CategoryDiff;
  hasChanges: boolean;
}

/**
 * V8.4.1 (complemento): acha no catálogo novo do provedor o filme correspondente a um filme que já temos (regra única usada pela
 * prévia e pela aplicação da atualização).
 */
export function findFreshMovieForExisting(
  m: Movie,
  freshIds: Set<string>,
  freshById: Map<string, Movie>,
  freshByTitle: Map<string, Movie>,
  existingById: Map<string, Movie>
): Movie | undefined {
  if (m.id && freshById.has(m.id)) return freshById.get(m.id);
  if (m.id && freshIds.has(m.id)) return undefined;
  const t = (m.title || '').toLowerCase().trim();
  const byTitle = t ? freshByTitle.get(t) : undefined;
  if (!byTitle) return undefined;
  if (byTitle.id && byTitle.id !== m.id && existingById.has(byTitle.id)) return undefined; // é de outro filme nosso
  return byTitle;
}

/**
 * Compara o catálogo existente com os novos dados buscados no servidor IPTV.
 * REGRA MANDATÓRIA: Não reorganiza nem remove o que o admin já tem.
 * Detecta APENAS itens genuinamente novos (não correspondentes por ID, URL ou nome base)
 * e alterações puramente técnicas (como novas URLs de stream).
 */
export function computeCatalogDiff(
  oldChannels: Channel[],
  newChannels: Channel[],
  oldMovies: Movie[],
  newMovies: Movie[],
  oldSeries: Series[],
  newSeries: Series[]
): CatalogDiff {
  // 1. CHANNELS: Indexa os canais existentes por ID, URL e Nome Base Canônico
  const oldChannelsById = new Map<string, Channel>();
  const oldChannelsByUrl = new Map<string, Channel>();
  const oldChannelsByBaseName = new Map<string, Channel>();

  for (const c of oldChannels) {
    if (c.id) oldChannelsById.set(c.id, c);
    if (c.streamUrl) oldChannelsByUrl.set(c.streamUrl.trim(), c);
    if (c.backupStreams) {
      for (const u of c.backupStreams) {
        if (u) oldChannelsByUrl.set(u.trim(), c);
      }
    }
    if (c.qualities) {
      for (const q of c.qualities) {
        if (q.streamUrl) oldChannelsByUrl.set(q.streamUrl.trim(), c);
      }
    }
    const bName = extractChannelQualityInfo(c.name).baseName.toLowerCase().trim();
    if (bName) oldChannelsByBaseName.set(bName, c);
  }

  const channelsAdded: DiffItem[] = [];
  const channelsChanged: DiffItem[] = [];
  const seenNewBaseNames = new Set<string>();

  for (const fresh of newChannels) {
    const url = (fresh.streamUrl || '').trim();
    const bName = extractChannelQualityInfo(fresh.name).baseName.toLowerCase().trim();

    const existing =
      (fresh.id && oldChannelsById.get(fresh.id)) ||
      (url && oldChannelsByUrl.get(url)) ||
      (bName && oldChannelsByBaseName.get(bName));

    if (!existing) {
      // Canal genuinamente novo! Evita adicionar múltiplas resoluções do mesmo canal novo
      if (!seenNewBaseNames.has(bName || fresh.id)) {
        seenNewBaseNames.add(bName || fresh.id);
        channelsAdded.push({
          id: fresh.id,
          name: fresh.name,
        });
      }
    } else {
      // Canal já existe: preserva 100% de nome e categoria organizados pelo usuário.
      // Apenas detecta se a URL do stream principal mudou.
      if (url && existing.streamUrl && url !== existing.streamUrl) {
        const hasUrl =
          existing.streamUrl === url ||
          existing.backupStreams?.includes(url) ||
          existing.qualities?.some((q) => q.streamUrl === url);
        if (!hasUrl && !channelsChanged.some((c) => c.id === existing.id)) {
          channelsChanged.push({
            id: existing.id,
            name: existing.name,
            changes: ['URL do stream atualizada'],
          });
        }
      }
    }
  }

  // 2. MOVIES: Indexa por ID, URL e título limpo
  const oldMoviesById = new Map<string, Movie>();
  const oldMoviesByTitle = new Map<string, Movie>();
  const oldMoviesByUrl = new Map<string, Movie>();

  for (const m of oldMovies) {
    if (m.id) oldMoviesById.set(m.id, m);
    if (m.streamUrl) oldMoviesByUrl.set(m.streamUrl.trim(), m);
    const cleanTitle = (m.title || '').toLowerCase().trim();
    if (cleanTitle) oldMoviesByTitle.set(cleanTitle, m);
  }

  const moviesAdded: DiffItem[] = [];
  const moviesChanged: DiffItem[] = [];
  const seenNewMovieTitles = new Set<string>();

  for (const fresh of newMovies) {
    const cleanTitle = (fresh.title || '').toLowerCase().trim();
    const url = (fresh.streamUrl || '').trim();
    const existing =
      (fresh.id && oldMoviesById.get(fresh.id)) ||
      (url && oldMoviesByUrl.get(url)) ||
      (cleanTitle && oldMoviesByTitle.get(cleanTitle));

    if (!existing) {
      if (cleanTitle && !seenNewMovieTitles.has(cleanTitle)) {
        seenNewMovieTitles.add(cleanTitle);
        moviesAdded.push({
          id: fresh.id,
          name: fresh.title,
        });
      }
    }
  }

  // V8.4.1 (complemento): "URL atualizada" usa a MESMA regra que a aplicação usa para trocar o endereço (applyPlaylistUpdate):
  //  - mesmo id no provedor e endereço diferente → atualiza;
  //  - só o título bate: atualiza apenas se o id antigo NÃO existe mais no provedor e o filme encontrado não é de
  //    outro filme que já temos (títulos repetidos, ex. dublado e legendado, não contam mais como "alterado").
  // Antes qualquer título igual com endereço diferente aparecia como alterado e, como aplicar não mudava o endereço,
  // os mesmos filmes voltavam toda vez.
  const freshMovieIds = new Set(newMovies.map((f) => f.id).filter(Boolean));
  const freshMoviesById = new Map<string, Movie>();
  const freshMoviesByTitle = new Map<string, Movie>();
  for (const f of newMovies) {
    if (f.id && !freshMoviesById.has(f.id)) freshMoviesById.set(f.id, f);
    const t = (f.title || '').toLowerCase().trim();
    if (t && !freshMoviesByTitle.has(t)) freshMoviesByTitle.set(t, f);
  }
  for (const m of oldMovies) {
    const fresh = findFreshMovieForExisting(m, freshMovieIds, freshMoviesById, freshMoviesByTitle, oldMoviesById);
    const url = (fresh?.streamUrl || '').trim();
    if (fresh && url && url !== (m.streamUrl || '').trim() && !moviesChanged.some((x) => x.id === m.id)) {
      moviesChanged.push({ id: m.id, name: m.title, changes: ['URL atualizada'] });
    }
  }

  // 3. SERIES: Indexa por ID e título limpo
  const oldSeriesById = new Map<string, Series>();
  const oldSeriesByTitle = new Map<string, Series>();

  for (const s of oldSeries) {
    if (s.id) oldSeriesById.set(s.id, s);
    const cleanTitle = (s.title || '').toLowerCase().trim();
    if (cleanTitle) oldSeriesByTitle.set(cleanTitle, s);
  }

  const seriesAdded: DiffItem[] = [];
  const seriesChanged: DiffItem[] = [];
  const seenNewSeriesTitles = new Set<string>();

  for (const fresh of newSeries) {
    const cleanTitle = (fresh.title || '').toLowerCase().trim();
    const existing =
      (fresh.id && oldSeriesById.get(fresh.id)) ||
      (cleanTitle && oldSeriesByTitle.get(cleanTitle));

    if (!existing) {
      if (cleanTitle && !seenNewSeriesTitles.has(cleanTitle)) {
        seenNewSeriesTitles.add(cleanTitle);
        seriesAdded.push({
          id: fresh.id,
          name: fresh.title,
        });
      }
    } else {
      if (fresh.seasonsCount && existing.seasonsCount && fresh.seasonsCount > existing.seasonsCount) {
        if (!seriesChanged.some((s) => s.id === existing.id)) {
          seriesChanged.push({
            id: existing.id,
            name: existing.title,
            changes: [`Temporadas: ${existing.seasonsCount} -> ${fresh.seasonsCount}`],
          });
        }
      }
    }
  }

  // 4. CATEGORIAS NOVAS (Apenas originadas de itens genuinamente novos)
  const newChannelsById = new Map(newChannels.map((c) => [c.id, c]));
  const newMoviesById = new Map(newMovies.map((m) => [m.id, m]));
  const newSeriesById = new Map(newSeries.map((s) => [s.id, s]));

  const oldLiveCats = new Set(oldChannels.map((c) => (c.groupTitle || '').trim()).filter(Boolean));
  const addedLiveCats = new Set(
    channelsAdded.map((item) => (newChannelsById.get(item.id)?.groupTitle || '').trim()).filter(Boolean)
  );

  const oldVodCats = new Set(oldMovies.map((m) => (m.category || '').trim()).filter(Boolean));
  const addedVodCats = new Set(
    moviesAdded.map((item) => (newMoviesById.get(item.id)?.category || '').trim()).filter(Boolean)
  );

  const oldSeriesCats = new Set(oldSeries.map((s) => (s.category || '').trim()).filter(Boolean));
  const addedSeriesCats = new Set(
    seriesAdded.map((item) => (newSeriesById.get(item.id)?.category || '').trim()).filter(Boolean)
  );

  const categories: CategoryDiff = {
    newLive: Array.from(addedLiveCats).filter((c) => !oldLiveCats.has(c)).sort((a, b) => a.localeCompare(b, 'pt-BR')),
    newVod: Array.from(addedVodCats).filter((c) => !oldVodCats.has(c)).sort((a, b) => a.localeCompare(b, 'pt-BR')),
    newSeries: Array.from(addedSeriesCats).filter((c) => !oldSeriesCats.has(c)).sort((a, b) => a.localeCompare(b, 'pt-BR')),
  };

  // Itens existentes NUNCA são removidos na atualização de lista para preservar o trabalho do admin
  const channelsRemoved: DiffItem[] = [];
  const moviesRemoved: DiffItem[] = [];
  const seriesRemoved: DiffItem[] = [];

  const hasChanges =
    channelsAdded.length > 0 ||
    channelsChanged.length > 0 ||
    moviesAdded.length > 0 ||
    moviesChanged.length > 0 ||
    seriesAdded.length > 0 ||
    seriesChanged.length > 0 ||
    categories.newLive.length > 0 ||
    categories.newVod.length > 0 ||
    categories.newSeries.length > 0;

  return {
    channels: { added: channelsAdded, changed: channelsChanged, removed: channelsRemoved },
    movies: { added: moviesAdded, changed: moviesChanged, removed: moviesRemoved },
    series: { added: seriesAdded, changed: seriesChanged, removed: seriesRemoved },
    categories,
    hasChanges,
  };
}
